import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { wrestlerAvatar, ASSETS } from "../lib/api";
import AvatarLayered from "../components/AvatarLayered";
import { BackToWrestler } from "../components/BackToWrestler";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { ArrowLeft, X, Zap, Flame, Lock, Sparkles } from "lucide-react";
import { visibleInterval } from "../lib/visibleInterval";
import { useInZoom } from "../components/MobileZoom";
import { PyroTutorial } from "../components/PyroTutorial";

/* ---------- Assets ---------- */
const CRACKER_IMG = {
  heavyweight:   "/pyro/Cracker%20Heavyweight.webp",
  middleweight:  "/pyro/Cracker%20Middleweight.webp",
  cruiserweight: "/pyro/Cracker%20Cruiserweight.webp",
  tag_team:      "/pyro/Cracker%20Tag%20Team.webp",
  wildcard:      "/pyro/Cracker%20Wildcard.webp",
};
const SPARK_IMG = {
  heavyweight:   "/pyro/Spark%20Heavyweight.webp",
  middleweight:  "/pyro/Spark%20Middleweight.webp",
  cruiserweight: "/pyro/Spark%20Cruiserweight.webp",
  tag_team:      "/pyro/Spark%20Tag%20Team.webp",
  wildcard:      "/pyro/Spark%20Wildcard.webp",
};
const CLASS_COLOR = {
  heavyweight: "#FF007F", middleweight: "#00F0FF", cruiserweight: "#FFD700",
  tag_team: "#39FF14", wildcard: "#9D00FF",
};
const CLASS_LABEL = {
  heavyweight: "HEAVYWEIGHT", middleweight: "MIDDLEWEIGHT", cruiserweight: "CRUISERWEIGHT",
  tag_team: "TAG TEAM", wildcard: "WILDCARD",
};

/* Step placements (percent of viewport). Keyed by class.
   Y-axis cascade from the entrance OUTWARD = cracker drops lower on screen.
   CW/TT sit on the highest step (closest to the entrance), then MW/WC a bit
   lower, and HW + reserved the lowest (nearest the stage floor). */
const CRACKER_SLOTS = {
  heavyweight:   { side: "left",  pos: 4,  bottom: 2  }, // outermost — lowest step
  middleweight:  { side: "left",  pos: 14, bottom: 5  },
  cruiserweight: { side: "left",  pos: 24, bottom: 8  }, // closest to entrance — highest step
  tag_team:      { side: "right", pos: 24, bottom: 8  },
  wildcard:      { side: "right", pos: 14, bottom: 5  },
  reserved:      { side: "right", pos: 4,  bottom: 2  },
};

/* ---------- Formatting helpers ---------- */
function fmtEnergy(v, decimals = 2) {
  const n = Number(v || 0);
  return n.toFixed(decimals);
}
function fmtBonusValue(spark, raw) {
  const n = Number(raw || 0);
  const t = spark.bonus_type;
  if (t === "stat_all_add" || t === "stat_class_add") return `+${n.toFixed(0)} ${spark.bonus_stat}`;
  if (t === "energy_per_min_add") return `+${n.toFixed(3)} ⚡/min`;
  return `+${n.toFixed(2)}%`;
}
function fmtBonusNext(spark) {
  if (!spark || spark.placeholder) return "—";
  return fmtBonusValue(spark, spark.next_bonus);
}
function fmtBonusCur(spark) {
  if (!spark || !spark.unlocked || spark.placeholder) return "—";
  return fmtBonusValue(spark, spark.current_bonus);
}
function bonusLabel(spark, cumulative = null) {
  // When `cumulative` is provided, inject the numeric bonus into the description
  // (e.g. "+2 fortune to ALL wrestlers").
  const t = spark.bonus_type;
  const cls = spark.bonus_class ? ` (${CLASS_LABEL[spark.bonus_class] || spark.bonus_class})` : "";
  const slot = spark.bonus_slot ? ` ${spark.bonus_slot}` : "";
  if (t === "placeholder") return "Placeholder — coming soon";
  // Format the cumulative value when we have it
  const hasCum = cumulative !== null && cumulative !== undefined && !spark.placeholder;
  const n = Number(cumulative || 0);
  const numAdd  = hasCum ? `+${n.toFixed(0)} ` : "+";
  const numPct  = hasCum ? `+${n.toFixed(2)}% ` : "+% ";
  const numRate = hasCum ? `+${n.toFixed(3)} ⚡/min ` : "+Energy / min ";
  if (t === "stat_all_add")       return `${numAdd}${spark.bonus_stat} to ALL wrestlers`;
  if (t === "stat_class_add")     return `${numAdd}${spark.bonus_stat}${cls}`;
  if (t === "stat_class_pct")     return `${numPct}${spark.bonus_stat}${cls}`;
  if (t === "gear_slot_pct")      return `${numPct}bonus on Gear${slot} stats`;
  if (t === "xp_bonus_pct_all")   return `${numPct}XP Gain for ALL`;
  if (t === "passive_gains_pct_all") return `${numPct}Passive Gains ALL`;
  if (t === "underdog_bonus_pct_all") return `${numPct}Underdog Bonus ALL`;
  if (t === "spark_bonus_boost_class") return `${numPct}higher bonus on ALL ${CLASS_LABEL[spark.bonus_class]} sparks`;
  if (t === "spark_material_discount_class") return `${numPct}reduced material cost on ${CLASS_LABEL[spark.bonus_class]} sparks`;
  if (t === "tag_together_pct")   return `${numPct}Tag Team Together Bonus`;
  if (t === "shop_restock_bonus_pct") return `${numPct}Daily Shop Restock Bonus`;
  if (t === "training_hotdog_pct")    return `${numPct}Hot Dog gain from Training`;
  if (t === "card_bonus_pct")         return `${numPct}Card Bonus (×)`;
  if (t === "double_spark_chance_pct") return `${numPct}chance to upgrade any Spark twice`;
  if (t === "double_energy_claim_chance_pct") return `${numPct}chance for doubled Energy on claim`;
  if (t === "energy_per_min_add") return numRate;
  return t;
}

/* =========================================================
   AMBIENT FX — rotating spotlight beams + sparkling dots
   ========================================================= */
function AmbientFX() {
  const dots = useMemo(() => Array.from({ length: 24 }).map((_, i) => ({
    id: i,
    left: Math.random() * 100,
    top: 10 + Math.random() * 80,
    size: 3 + Math.random() * 4,
    delay: Math.random() * 6,
    duration: 4 + Math.random() * 5,
    color: ["#FFD700", "#FF9500", "#00F0FF", "#FF007F"][Math.floor(Math.random() * 4)],
  })), []);
  return (
    <>
      <div className="pointer-events-none fixed inset-0 overflow-hidden z-[2]" aria-hidden="true">
        <div className="absolute left-1/2 top-0 -translate-x-1/2" style={{
          width: 24, height: "calc(var(--vh, 1vh) * 60)",
          background: "linear-gradient(180deg, rgba(255,215,0,0.55), rgba(255,149,0,0.0) 85%)",
          filter: "blur(14px)", transformOrigin: "top center",
          animation: "pyro-beam-sweep 7s ease-in-out infinite",
        }} />
        <div className="absolute left-1/2 top-0 -translate-x-1/2" style={{
          width: 24, height: "calc(var(--vh, 1vh) * 60)",
          background: "linear-gradient(180deg, rgba(0,240,255,0.5), rgba(0,240,255,0.0) 85%)",
          filter: "blur(14px)", transformOrigin: "top center",
          animation: "pyro-beam-sweep 9s -3s ease-in-out infinite reverse",
        }} />
        {dots.map((d) => (
          <span key={d.id} style={{
            position: "absolute",
            left: `${d.left}%`, top: `${d.top}%`,
            width: d.size, height: d.size,
            background: d.color, borderRadius: "50%",
            boxShadow: `0 0 8px ${d.color}, 0 0 16px ${d.color}88`,
            animation: `pyro-twinkle ${d.duration}s ease-in-out ${d.delay}s infinite`,
            opacity: 0,
          }} />
        ))}
      </div>
      <style>{`
        @keyframes pyro-beam-sweep {
          0%, 100% { transform: translateX(-50%) rotate(-16deg); opacity: 0.35; }
          50%      { transform: translateX(-50%) rotate(16deg);  opacity: 0.65; }
        }
        @keyframes pyro-twinkle {
          0%, 100% { opacity: 0; transform: scale(0.6); }
          50%      { opacity: 0.95; transform: scale(1.1); }
        }
        @keyframes pyro-spark-rise {
          0%   { opacity: 0; transform: translate(-50%, 20px) scale(0.3); }
          20%  { opacity: 1; transform: translate(-50%, 0) scale(1.5); }
          80%  { opacity: 1; transform: translate(-50%, -60px) scale(1.1); }
          100% { opacity: 0; transform: translate(-50%, -100px) scale(0.6); }
        }
        @keyframes pyro-spark-pop {
          0%   { transform: scale(1); }
          20%  { transform: scale(1.6); }
          50%  { transform: scale(1.1); }
          100% { transform: scale(1); }
        }
        @keyframes pyro-bolt-pulse {
          0%, 100% { filter: drop-shadow(0 0 6px #FFD700) drop-shadow(0 0 14px #FFD70088); transform: scale(1); }
          50%      { filter: drop-shadow(0 0 12px #FFD700) drop-shadow(0 0 28px #FFD700); transform: scale(1.08); }
        }
        @keyframes pyro-active-ring {
          0%, 100% { box-shadow: 0 0 12px #FFD700, 0 0 28px #FFD70088; border-color: #FFD700; }
          50%      { box-shadow: 0 0 24px #FF9500, 0 0 48px #FF9500AA; border-color: #FF9500; }
        }
        @keyframes pyro-tick-float {
          0%   { opacity: 0; transform: translate(-50%, 10px) scale(0.8); }
          20%  { opacity: 1; transform: translate(-50%, -4px) scale(1.1); }
          80%  { opacity: 1; transform: translate(-50%, -34px) scale(1); }
          100% { opacity: 0; transform: translate(-50%, -56px) scale(0.9); }
        }
        @keyframes pyro-energy-add {
          0%   { color: #FFD700; text-shadow: 0 0 20px #FFD700, 0 0 36px #FFD700AA; transform: scale(1.15); }
          100% { color: #FF9500; text-shadow: 0 0 10px #FF950088; transform: scale(1); }
        }
        @keyframes pyro-hover-breathe {
          0%, 100% { transform: scale(1);     filter: drop-shadow(0 0 2px rgba(255,255,255,0)); }
          50%      { transform: scale(1.06);  filter: drop-shadow(0 0 10px rgba(255,215,0,0.45)); }
        }
        @keyframes pyro-selected-glow {
          0%, 100% { filter: drop-shadow(0 0 10px #FFD700) drop-shadow(0 0 18px #FFD70088); }
          50%      { filter: drop-shadow(0 0 18px #FFD700) drop-shadow(0 0 36px #FFD700DD); }
        }
        @keyframes pyro-billboard-tick-rise {
          0%   { opacity: 0; transform: translate(-50%, 10px) scale(0.9); }
          15%  { opacity: 1; transform: translate(-50%, 0) scale(1.05); }
          80%  { opacity: 1; transform: translate(-50%, -22px) scale(1); }
          100% { opacity: 0; transform: translate(-50%, -36px) scale(0.9); }
        }
      `}</style>
    </>
  );
}

/* =========================================================
   SPARK TREE — vertical column of sparks ABOVE the cracker image.
   Positioned over the top center of the cracker so the first spark
   sits visually at the cracker's fuse. Never clipped (overflow visible).
   ========================================================= */
function SparkTree({ cls, sparks, onPickSpark, pulseIdx, pulse, selectedIdx }) {
  const color = CLASS_COLOR[cls];
  return (
    <div
      className="absolute left-1/2 -translate-x-1/2 flex flex-col-reverse items-center gap-1.5 pointer-events-none z-30"
      style={{ bottom: "calc(50% - 1.5rem)" }}
    >
      {sparks.map((s, idx) => {
        const unlocked = s.unlocked;
        const isPulse = pulseIdx === idx;
        const pulseGained = isPulse ? (pulse?.gained || 1) : 0;
        const pulseDoubled = isPulse && !!pulse?.doubled;
        const greyedPlaceholder = unlocked && s.placeholder;
        const isSelected = selectedIdx === idx && unlocked && !greyedPlaceholder;
        let animation = undefined;
        if (isPulse) animation = "pyro-spark-pop 1.6s ease-out";
        else if (isSelected) animation = "pyro-selected-glow 1.8s ease-in-out infinite";
        return (
          <button
            key={idx}
            data-testid={`pyro-spark-${cls}-${idx}`}
            disabled={!unlocked}
            onClick={(e) => { e.stopPropagation(); onPickSpark(idx); }}
            className={
              "pointer-events-auto group relative w-12 h-12 flex items-center justify-center transition-transform duration-300 " +
              (unlocked && !greyedPlaceholder && !isSelected ? "hover:[animation:pyro-hover-breathe_1.8s_ease-in-out_infinite]" : "")
            }
            style={{
              opacity: unlocked ? 1 : 0.3,
              transform: isSelected ? "scale(1.18)" : undefined,
              animation,
            }}
            title={unlocked ? `${s.name} · Lv ${s.level}` : "Locked"}
          >
            {unlocked ? (
              <>
                <img
                  src={SPARK_IMG[cls]}
                  alt={s.name}
                  className="w-12 h-12 object-contain"
                  style={{
                    filter: greyedPlaceholder
                      ? "grayscale(1) brightness(0.6)"
                      : isPulse
                        ? `drop-shadow(0 0 14px #FFFFFF) drop-shadow(0 0 24px ${color})`
                        : `drop-shadow(0 0 7px ${color}) drop-shadow(0 0 14px ${color}88)`,
                    opacity: greyedPlaceholder ? 0.55 : 1,
                  }}
                />
                <span
                  className="absolute inset-0 flex items-center justify-center font-heading font-bold leading-none pointer-events-none select-none"
                  style={{
                    fontSize: "15px",
                    color: "#FFFFFF",
                    textShadow: `0 0 4px #000, 0 0 8px ${color}, 0 1px 2px #000`,
                    letterSpacing: "0.5px",
                  }}
                  data-testid={`pyro-spark-level-${cls}-${idx}`}
                >
                  {s.level}
                </span>
                {/* +N float on upgrade */}
                {isPulse && (
                  <span
                    className="absolute left-1/2 pointer-events-none z-40 font-heading font-black whitespace-nowrap"
                    style={{
                      top: -8,
                      fontSize: pulseDoubled ? "20px" : "16px",
                      color: pulseDoubled ? "#9D00FF" : "#FFD700",
                      textShadow: pulseDoubled
                        ? "0 0 10px #9D00FF, 0 0 20px #FFFFFF"
                        : "0 0 8px #FFD700, 0 0 14px #FFFFFF",
                      animation: "pyro-spark-rise 1.6s ease-out forwards",
                    }}
                    data-testid={`pyro-spark-float-${cls}-${idx}`}
                  >
                    +{pulseGained}{pulseDoubled ? " ✨" : ""}
                  </span>
                )}
              </>
            ) : (
              <div className="w-8 h-8 rounded-full border-2 border-dashed flex items-center justify-center"
                   style={{ borderColor: color + "66" }}>
                <Lock size={12} style={{ color: color + "AA" }} />
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}

/* =========================================================
   CRACKER — placed absolutely on a step. Only the BOTTOM half
   of the cracker image acts as the clickable "buy next" button.
   ========================================================= */
function Cracker({ cls, data, onClickCracker, pulseIdx, pulse, slot, selected }) {
  const color = CLASS_COLOR[cls];
  const label = CLASS_LABEL[cls];
  if (!data) return null;

  const isUnlockSelected = !!(selected && selected.cls === cls && selected.kind === "unlock");
  const selectedSparkIdx = (selected && selected.cls === cls && selected.kind === "upgrade")
    ? selected.spark_idx
    : null;

  // Positioning (percent of viewport)
  const posStyle = {
    position: "absolute",
    bottom: `calc(var(--vh, 1vh) * ${slot.bottom})`,
    [slot.side]: `calc(var(--vw, 1vw) * ${slot.pos})`,
    width: "clamp(60px, calc(var(--vw, 1vw) * 7.5), 110px)",
    zIndex: 5,
  };

  return (
    <div className="relative" style={posStyle} data-testid={`pyro-cracker-${cls}`}>
      {/* Pedestal / step */}
      <div
        className="absolute left-1/2 -translate-x-1/2"
        style={{
          bottom: -14, width: "130%", height: 22, borderRadius: 6,
          background: "linear-gradient(180deg, #2A1944 0%, #0A051A 90%)",
          border: "2px solid #39194D",
          boxShadow: `0 0 14px ${color}44`,
        }}
      />
      {/* Spark tree — rendered on top of everything (z-30) */}
      <SparkTree
        cls={cls}
        sparks={data.sparks}
        onPickSpark={(idx) => onClickCracker(cls, idx)}
        pulseIdx={pulseIdx}
        pulse={pulse}
        selectedIdx={selectedSparkIdx}
      />
      {/* Cracker image + buy-base button covering only bottom half */}
      <div
        className={
          "relative w-full transition-transform duration-300 " +
          (!isUnlockSelected && !data.all_unlocked ? "group hover:[animation:pyro-hover-breathe_2.2s_ease-in-out_infinite]" : "")
        }
        style={{
          transform: isUnlockSelected ? "scale(1.08)" : undefined,
          animation: isUnlockSelected ? "pyro-selected-glow 1.8s ease-in-out infinite" : undefined,
          filter: isUnlockSelected
            ? `drop-shadow(0 0 14px ${color}) drop-shadow(0 0 26px ${color}AA)`
            : undefined,
        }}
      >
        <img src={CRACKER_IMG[cls]} alt={label}
             className="w-full h-auto block drop-shadow-[0_6px_14px_rgba(0,0,0,0.9)]" />
        {/* Label chip overlaying the bottom of the cracker so it never clips */}
        <div className="absolute left-1/2 -translate-x-1/2 bottom-1 flex flex-col items-center gap-0.5 pointer-events-none z-10">
          <div
            className="px-1.5 py-0.5 rounded text-[8px] font-heading uppercase tracking-widest border whitespace-nowrap"
            style={{
              background: "#0A051AEE", color, borderColor: color + "AA",
              textShadow: `0 0 4px ${color}`,
            }}
          >
            {label}
          </div>
          <div
            className="px-1.5 py-0.5 rounded text-[9px] font-mono flex items-center gap-1 whitespace-nowrap"
            style={{ background: "#0A051AEE", color: "#FFD700", border: "1px solid #FFD70088" }}
          >
            {data.all_unlocked ? "MAX" : data.next_unlock_cost === 0 ? "FREE" : <><img src={ASSETS.currency.hotdog} alt="" className="w-3.5 h-3.5" /> {(data.next_unlock_cost || 0).toLocaleString()}</>}
          </div>
        </div>
        {/* Clickable buy-base — bottom 55% of cracker image (label chips use pointer-events:none so clicks pass through) */}
        <button
          data-testid={`pyro-cracker-base-${cls}`}
          onClick={() => onClickCracker(cls, null)}
          className="absolute left-0 right-0 bottom-0 h-[55%] cursor-pointer z-0"
          style={{ background: "transparent" }}
          aria-label={`Buy next ${label} spark`}
        />
      </div>
    </div>
  );
}

/* Empty "reserved" slot placed on the top-right step */
function ReservedSlot({ slot }) {
  const posStyle = {
    position: "absolute",
    bottom: `calc(var(--vh, 1vh) * ${slot.bottom})`,
    [slot.side]: `calc(var(--vw, 1vw) * ${slot.pos})`,
    width: "clamp(60px, calc(var(--vw, 1vw) * 7.5), 110px)",
    zIndex: 5,
  };
  return (
    <div className="relative pointer-events-none opacity-50" style={posStyle} data-testid="pyro-reserved-slot">
      <div
        className="absolute left-1/2 -translate-x-1/2"
        style={{
          bottom: -14, width: "130%", height: 22, borderRadius: 6,
          background: "linear-gradient(180deg, #2A1944 0%, #0A051A 90%)",
          border: "2px solid #39194D",
        }}
      />
      <div className="relative w-full aspect-[3/5] rounded-lg border-2 border-dashed flex items-center justify-center"
           style={{ borderColor: "#9D00FF66" }}>
        <div className="text-[8px] font-mono text-[#9D00FF] text-center leading-tight">COMING<br/>SOON</div>
        <div className="absolute left-1/2 -translate-x-1/2 bottom-1 px-1.5 py-0.5 rounded text-[8px] font-heading uppercase tracking-widest border whitespace-nowrap"
             style={{ background: "#0A051AEE", color: "#9D00FF", borderColor: "#9D00FFAA" }}>
          RESERVED
        </div>
      </div>
    </div>
  );
}

/* =========================================================
   BILLBOARD — positioned over the BG billboard. Pixel-aligned
   to the pyroBG screen (BG aspect 2848x1580 ≈ 1.8:1).
   ========================================================= */
function Billboard({ state, selected, assigned, upgradeOwned, poolDisplay, onBuyNext, onUpgrade, onDeselect, highlightEnergyKey, tickFloat }) {
  const cap = state?.energy_cap || 5;
  const pool = Number(poolDisplay ?? state?.energy_pool ?? 0);
  const whole = Math.floor(pool);
  const frac = pool - whole;
  const rate = (state?.assigned || []).reduce((s, w) => s + (w.is_active ? (w.active_rate_per_min || 0) : (w.passive_rate_per_min || 0)), 0) || state?.energy_rate_per_min_active || 0;
  const nextInMin = rate > 0 ? ((1 - frac) / rate) : null;
  const freeSparkRemaining = Number(state?.free_sparks_remaining || 0);
  const tutorialFree = state?.tutorial === "upgrade";
  const freeSparkAvailable = (freeSparkRemaining > 0 || tutorialFree) && selected?.kind === "upgrade" && !selected?.spark?.placeholder;
  const colNeed = selected?.spark?.upgrade_qty || 0;
  const colOwned = upgradeOwned ?? 0;

  return (
    <div
      className="absolute left-1/2 -translate-x-1/2 z-20"
      style={{
        top: "12px",
        width: "clamp(300px, calc(var(--vw, 1vw) * 36), 540px)",
      }}
      data-testid="pyro-billboard"
    >
      <div
        className="relative rounded-xl border-2 p-3 backdrop-blur-md"
        style={{
          background: "linear-gradient(180deg, rgba(10,5,26,0.86), rgba(10,5,26,0.72))",
          borderColor: "#FFD700",
          boxShadow: "0 0 24px rgba(255,215,0,0.4), inset 0 0 16px rgba(255,149,0,0.18)",
        }}
      >
        {/* Bulbs at corners */}
        {["left-1.5 top-1.5", "right-1.5 top-1.5", "left-1.5 bottom-1.5", "right-1.5 bottom-1.5"].map((p, i) => (
          <div key={i} className={`absolute ${p} w-1.5 h-1.5 rounded-full`}
               style={{ background: "#FFD700", boxShadow: "0 0 6px #FFD700" }} />
        ))}

        {/* HEADER — label + rate */}
        <div className="flex items-center justify-between gap-2 mb-1">
          <div className="flex items-center gap-2">
            <Flame className="text-[#FF9500]" size={18} style={{ filter: "drop-shadow(0 0 6px #FF9500)" }} />
            <span className="font-heading text-[11px] uppercase tracking-[0.25em] text-[#FF9500]"
                  style={{ textShadow: "0 0 8px #FF9500" }}>Pyro Energy</span>
          </div>
          <div className="text-[9px] font-mono text-[#A89BCC]" data-testid="pyro-rate">
            <span className="text-[#FFD700]">{rate.toFixed(3)}</span>⚡/min · {assigned} 🤼
          </div>
        </div>

        {/* MAIN NUMBER + BOLTS */}
        <div className="flex items-end justify-between gap-3 mb-2">
          <div className="relative">
            {/* Floating tick +N ⚡ synced to the active wrestler's gain */}
            {tickFloat && tickFloat.value > 0 && (
              <span
                key={`bb-tick-${tickFloat.id}`}
                className="absolute left-1/2 pointer-events-none z-30 font-heading font-black whitespace-nowrap"
                style={{
                  top: -14,
                  fontSize: "14px",
                  color: "#FFD700",
                  textShadow: "0 0 8px #FFD700, 0 0 16px #FF9500AA",
                  animation: "pyro-billboard-tick-rise 1.5s ease-out forwards",
                }}
                data-testid="pyro-billboard-energy-tick"
              >
                +{tickFloat.value.toFixed(4)} ⚡
              </span>
            )}
            <div
              key={highlightEnergyKey}
              className="font-heading leading-none"
              style={{
                fontSize: "clamp(28px, calc(var(--vw, 1vw) * 3.6), 44px)",
                color: "#FFD700",
                textShadow: "0 0 12px #FFD70088",
                animation: highlightEnergyKey ? "pyro-energy-add 1.6s ease-out" : undefined,
              }}
              data-testid="pyro-energy-amount"
            >
              {fmtEnergy(pool)}
              <span className="text-[#A89BCC] font-mono text-xs"> / {cap}</span>
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0" data-testid="pyro-energy-bolts">
            {Array.from({ length: cap }).map((_, i) => (
              <Zap
                key={i}
                size={18}
                style={{
                  color: i < whole ? "#FFD700" : "#3A2A5A",
                  animation: i < whole ? "pyro-bolt-pulse 2.4s ease-in-out infinite" : undefined,
                }}
                fill={i < whole ? "#FFD700" : "transparent"}
              />
            ))}
          </div>
        </div>

        {/* PROGRESS to next bolt */}
        <div className="h-2 rounded-full bg-[#0A051A] overflow-hidden border border-[#FFD70055]">
          <div className="h-full transition-all"
            style={{
              width: pool >= cap ? "100%" : `${(frac * 100).toFixed(1)}%`,
              background: "linear-gradient(90deg, #FF9500, #FFD700)",
              boxShadow: "0 0 10px #FFD70088",
            }}
          />
        </div>
        <div className="mt-1 text-center text-[9px] font-mono text-[#A89BCC]">
          {pool >= cap ? (
            <span className="text-[#FF007F]">FULL — spend sparks to make room</span>
          ) : rate > 0 && nextInMin !== null ? (
            <>next ⚡ in <span className="text-[#FFD700]">{nextInMin < 1 ? `${(nextInMin * 60).toFixed(0)}s` : `${nextInMin.toFixed(1)}m`}</span></>
          ) : (
            <span className="text-[#695D8E]">Assign wrestlers below</span>
          )}
        </div>

        {/* UPGRADE / UNLOCK PANEL */}
        {selected && (
          <div className="mt-3 border-t pt-2.5" style={{ borderColor: "#FFD70044" }} data-testid="pyro-billboard-upgrade">
            {selected.kind === "unlock" ? (
              <div className="flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-[9px] font-mono text-[#A89BCC] uppercase tracking-wider truncate">Unlock next · {CLASS_LABEL[selected.cls]}</div>
                  <div className="font-heading text-base text-[#FF9500] flex items-center gap-1.5" style={{ textShadow: "0 0 6px #FF9500" }}>
                    {selected.cost === 0 ? <span data-testid="pyro-unlock-free">FREE</span> : <><img src={ASSETS.currency.hotdog} alt="" className="w-5 h-5" /> {selected.cost.toLocaleString()}</>}
                  </div>
                  <div className="text-[9px] font-mono text-[#695D8E]">spark #{selected.spark_number} / 10</div>
                </div>
                <button data-testid="pyro-buy-unlock-btn" onClick={onBuyNext}
                        className="btn-neon px-3 py-2 text-[10px] font-bold">BUY</button>
                <button onClick={onDeselect} className="text-[#A89BCC] hover:text-white" aria-label="Close"
                        data-testid="pyro-billboard-close"><X size={14} /></button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-heading text-xs truncate"
                       style={{ color: CLASS_COLOR[selected.cls], textShadow: `0 0 4px ${CLASS_COLOR[selected.cls]}88` }}>
                    {selected.spark.name} <span className="text-[#A89BCC] text-[9px] font-mono">Lv {selected.spark.level}</span>
                  </div>
                  <div className="text-[10px] font-mono text-[#E5DCFF] truncate" data-testid="pyro-billboard-bonus-desc">
                    {bonusLabel(selected.spark, selected.spark.current_bonus)}
                  </div>
                </div>
                <div className={`flex flex-col gap-1 shrink-0 font-mono ${freeSparkAvailable ? "line-through opacity-40" : ""}`} data-testid="pyro-upgrade-cost">
                  <div className="flex items-center gap-1 px-1.5 py-0.5 rounded border text-[11px]"
                       style={{ borderColor: pool >= 1 ? "#FFD70088" : "#FF007F", background: "#0A051A", color: pool >= 1 ? "#FFD700" : "#FF007F" }}>
                    <Zap size={12} fill="currentColor" /> <span data-testid="pyro-upgrade-energy">{Math.floor(pool)}/1</span>
                  </div>
                  <div className="flex items-center gap-1 px-1.5 py-0.5 rounded border text-[11px] font-bold"
                       title={`${selected.spark.name}: you own ${colOwned}, need ${colNeed}`}
                       style={{ borderColor: colOwned >= colNeed ? "#39FF1488" : "#FF007F", background: "#0A051A" }}>
                    <img src={`/collectibles/${selected.spark.collectible_id}.webp`} alt={selected.spark.name}
                         className="w-5 h-5 rounded object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                    <span data-testid="pyro-upgrade-qty" style={{ color: colOwned >= colNeed ? "#39FF14" : "#FF007F" }}>
                      {colOwned.toLocaleString()}/{colNeed.toLocaleString()}
                    </span>
                  </div>
                </div>
                <button data-testid="pyro-upgrade-btn" onClick={onUpgrade}
                        disabled={selected.spark.placeholder || (!freeSparkAvailable && (pool < 1 || colOwned < colNeed))}
                        className="btn-neon px-3 py-2 text-[10px] font-bold disabled:opacity-40 disabled:cursor-not-allowed disabled:grayscale relative">
                  {freeSparkAvailable ? "FREE" : "UP"}
                  {freeSparkAvailable && !tutorialFree && (
                    <span className="absolute -top-1 -right-1 bg-[#39FF14] text-[#0A051A] text-[7px] font-mono font-bold rounded-full px-1 leading-tight"
                          data-testid="pyro-free-sparks-remaining"
                          style={{ boxShadow: "0 0 6px #39FF14" }}>
                      {freeSparkRemaining}
                    </span>
                  )}
                </button>
                <button onClick={onDeselect} className="text-[#A89BCC] hover:text-white" aria-label="Close"
                        data-testid="pyro-billboard-close"><X size={14} /></button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* =========================================================
   STAGE WRESTLER CARD — avatar + rate; active one is larger.
   ========================================================= */
function StageWrestler({ w, promo, active, tickFloat }) {
  const color = CLASS_COLOR[w.class_id] || "#9D00FF";
  const rate = active ? w.active_rate_per_min : w.passive_rate_per_min;
  const size = active ? "w-20 h-20 sm:w-24 sm:h-24" : "w-14 h-14 sm:w-16 sm:h-16";
  return (
    <div className="relative flex flex-col items-center group" data-testid={`pyro-wrestler-${w.wrestler_id}`}>
      {/* Energy tick float: gold for the active wrestler, smaller orange for passive ones */}
      {tickFloat && tickFloat.value > 0 && (
        <div
          key={tickFloat.id}
          data-testid={`pyro-tick-float-${w.wrestler_id}`}
          className={`absolute left-1/2 pointer-events-none z-30 font-heading font-bold whitespace-nowrap ${active ? "text-xs" : "text-[10px]"}`}
          style={{
            top: active ? -14 : -12,
            color: active ? "#FFD700" : "#FF9500",
            textShadow: active ? "0 0 8px #FFD700, 0 0 14px #FFD700AA" : "0 0 6px #FF9500AA",
            animation: "pyro-tick-float 1.5s ease-out forwards",
          }}
        >
          +{tickFloat.value.toFixed(4)} ⚡
        </div>
      )}
      {/* Avatar (active wrestler is fixed — set by coming from the Wrestler page) */}
      <div
        className="relative"
        aria-label={active ? "Active" : w.name}
        data-testid={`pyro-wrestler-select-${w.wrestler_id}`}
      >
        <AvatarLayered
          fgSrc={wrestlerAvatar(w)}
          promo={promo}
          className={`${size} rounded-full border-2 transition-all`}
          style={{
            borderColor: active ? "#FFD700" : color,
            boxShadow: active
              ? "0 0 18px #FFD700, 0 0 36px #FFD70088"
              : `0 0 8px ${color}88`,
            animation: active ? "pyro-active-ring 2.2s ease-in-out infinite" : undefined,
          }}
          alt={w.name}
        />
        {/* Active ring badge */}
        {active && (
          <span className="absolute -top-1.5 -left-1.5 bg-[#FFD700] text-[#0A051A] text-[8px] font-heading font-bold px-1 rounded uppercase tracking-wider"
                style={{ boxShadow: "0 0 10px #FFD700" }}>
            ACTIVE
          </span>
        )}
      </div>
      {/* Name */}
      <div className="mt-1 text-[9px] font-heading text-white truncate max-w-[88px] text-center"
           style={{ textShadow: "0 1px 3px #000" }}>{w.name}</div>
      {/* Rate */}
      <div
        className="text-[10px] font-mono font-bold leading-tight"
        style={{
          color: active ? "#FFD700" : "#A3A3C8",
          textShadow: active ? "0 0 6px #FFD700" : "0 1px 3px #000",
        }}
        title={active ? "Active — full rate to pool" : `Passive — ${w.passive_pct}% of base`}
      >
        +{rate.toFixed(4)}⚡/min
      </div>
    </div>
  );
}

/* =========================================================
   PYRO PAGE
   ========================================================= */
export default function PyroPage() {
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [state, setState] = useState(null);
  const [owned, setOwned] = useState({});
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [pulse, setPulse] = useState({ cls: null, idx: null });
  const [tut, setTut] = useState(null);
  useEffect(() => { if (state?.tutorial) setTut(state.tutorial); }, [state?.tutorial]);

  // Optimistic client-side energy display (ticks every second)
  const [poolDisplay, setPoolDisplay] = useState(0);
  const [tickFloats, setTickFloats] = useState({ id: 0, value: 0 });
  const [energyKey, setEnergyKey] = useState(0);
  const tickIdRef = useRef(0);
  const authoritativeLoadedAt = useRef(0);

  // Dynamic offset so the stage fits exactly below the TopMenu on any viewport.
  const [topOffset, setTopOffset] = useState(0);
  const inZoom = useInZoom();
  useEffect(() => {
    const measure = () => {
      const el = document.querySelector('[data-testid="top-menu"]');
      if (el) {
        const r = el.getBoundingClientRect();
        setTopOffset(Math.max(0, Math.round(r.bottom)));
      }
    };
    // Measure a couple of times after mount to catch late layout (fonts, images).
    measure();
    const t1 = setTimeout(measure, 100);
    const t2 = setTimeout(measure, 400);
    window.addEventListener("resize", measure);
    return () => { clearTimeout(t1); clearTimeout(t2); window.removeEventListener("resize", measure); };
  }, []);

  const load = async () => {
    const [s, cols] = await Promise.all([
      api.get("/pyro/state"),
      api.get("/collectibles"),
    ]);
    setState(s.data);
    setPoolDisplay(Number(s.data.energy_pool || 0));
    authoritativeLoadedAt.current = Date.now();
    const map = {};
    (cols.data || []).forEach((c) => { map[c.id] = c.qty || 0; });
    setOwned(map);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    return visibleInterval(load, 10000);
  }, []);

  /* Client-side 1s tick: compute energy gain from the active wrestler and
     show feedback. The server is the source of truth; we re-sync every poll. */
  useEffect(() => {
    if (!state?.unlocked || !state.assigned?.length) return;
    const active = state.assigned.find((w) => w.is_active);
    if (!active) return;
    const perSecActive = (active.active_rate_per_min || 0) / 60.0;
    // Inactive wrestlers on stage tick live into the pool at their passive rate.
    const perSecPassive = state.assigned.filter((w) => !w.is_active).reduce((s, w) => s + (w.passive_rate_per_min || 0), 0) / 60.0;
    const cap = state.energy_cap || 5;
    const timer = setInterval(() => {
      setPoolDisplay((cur) => {
        const next = Math.max(cur, Math.min(cap, cur + perSecActive + perSecPassive));
        return next;
      });
      tickIdRef.current += 1;
      setTickFloats({ id: tickIdRef.current, value: perSecActive });
      setEnergyKey((k) => k + 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [state?.assigned?.map(w => `${w.wrestler_id}:${w.is_active}`).join(","), state?.energy_cap]);

  if (loading) return <div className="min-h-screen p-8 text-center font-heading neon-cyan bg-[#0A051A]">LIGHTING THE FUSE...</div>;
  if (!state?.unlocked) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8 text-center" style={{ background: "#0A051A" }}>
        <div className="card-surface p-8 max-w-md" style={{ borderColor: "#FF9500" }}>
          <Flame size={48} className="mx-auto mb-3 text-[#FF9500]" style={{ filter: "drop-shadow(0 0 8px #FF9500)" }} />
          <h2 className="font-heading text-3xl neon-yellow mb-2">PYRO LOCKED</h2>
          <p className="text-[#A89BCC] font-mono text-sm mb-5">
            Visit the Handshake Shop to unlock the Pyro activity.
          </p>
          <button onClick={() => nav("/shop")} className="btn-neon px-5 py-2.5">OPEN SHOP</button>
        </div>
      </div>
    );
  }

  const handleClickCracker = (cls, sparkIdx) => {
    const cracker = state.crackers[cls];
    if (sparkIdx === null) {
      if (cracker.all_unlocked) {
        toast.info("All sparks unlocked for this class");
        setSelected(null);
        return;
      }
      setSelected({
        cls, kind: "unlock",
        spark_number: cracker.unlocked_count + 1,
        cost: cracker.next_unlock_cost,
      });
    } else {
      const spark = cracker.sparks[sparkIdx];
      if (!spark || !spark.unlocked) return;
      setSelected({ cls, kind: "upgrade", spark_idx: sparkIdx, spark });
    }
  };

  const buyNextSpark = async () => {
    if (!selected || selected.kind !== "unlock") return;
    try {
      const r = await api.post("/pyro/cracker/buy", { class_id: selected.cls });
      toast.success(`${state.crackers[selected.cls].sparks[r.data.spark_idx]?.name || "Spark"} unlocked!`, {
        description: r.data.hot_dogs_spent ? `-${r.data.hot_dogs_spent.toLocaleString()} Hot Dogs` : "Free!",
      });
      setPulse({ cls: selected.cls, idx: r.data.spark_idx });
      setTimeout(() => setPulse({ cls: null, idx: null }), 1700);
      await load();
      await refresh();
      setSelected(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const upgradeSpark = async () => {
    if (!selected || selected.kind !== "upgrade") return;
    try {
      const r = await api.post("/pyro/spark/upgrade", {
        class_id: selected.cls,
        spark_idx: selected.spark_idx,
      });
      const gained = r.data.doubled ? 2 : 1;
      toast.success(
        r.data.doubled
          ? `✨🎇 GLITTERBOMB! ${selected.spark.name} → Lv ${r.data.new_level}`
          : `${selected.spark.name} → Lv ${r.data.new_level}`,
        {
          description: r.data.doubled
            ? (r.data.was_free ? "DOUBLE UPGRADE triggered! +2 levels, free!" : `DOUBLE UPGRADE triggered! +2 levels for 1 cost · -${r.data.collectible_spent.qty} items · -1 ⚡`)
            : r.data.was_free ? "Free upgrade!" : `-${r.data.collectible_spent.qty} items · -1 ⚡`,
          duration: r.data.doubled ? 5000 : 3000,
          style: r.data.doubled ? { background: "linear-gradient(135deg,#2A1944,#9D00FF55)", border: "2px solid #9D00FF", color: "#FFFFFF" } : undefined,
        }
      );
      // Trigger pulse + "+N" float on the spark that was upgraded
      setPulse({ cls: selected.cls, idx: selected.spark_idx, gained, doubled: !!r.data.doubled });
      setTimeout(() => setPulse({ cls: null, idx: null }), 2000);
      // Keep the upgrade panel OPEN — refresh selected with the new spark data after load
      const prev = selected;
      await load();
      await refresh();
      // We re-select the SAME spark on the next render using a ref to the freshly loaded state
      setTimeout(() => {
        setState((s) => {
          if (!s) return s;
          const freshSpark = s.crackers?.[prev.cls]?.sparks?.[prev.spark_idx];
          if (freshSpark) {
            setSelected({ cls: prev.cls, kind: "upgrade", spark_idx: prev.spark_idx, spark: freshSpark });
          }
          return s;
        });
      }, 50);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const promo = user?.promotion || null;

  return (
    <div
      className={inZoom ? "relative overflow-hidden" : "fixed left-0 right-0 bottom-0 overflow-hidden"}
      style={{
        ...(inZoom ? { height: "calc(var(--vh) * 100)" } : { top: `${topOffset}px` }),
        backgroundImage: "url(/pyro/pyroBG.webp)",
        backgroundSize: "cover",
        backgroundPosition: "center bottom",
        backgroundRepeat: "no-repeat",
        backgroundColor: "#0A051A",
      }}
      data-testid="pyro-page"
    >
      <BackToWrestler className="absolute top-3 left-3 z-40" testId="pyro-back-btn" />
      {/* Dark gradient so content stays legible */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A051A]/55 via-transparent to-[#0A051A]/90 z-[1]" aria-hidden="true" />

      <AmbientFX />

      {/* Billboard — pixel-anchored above the entrance */}
      <Billboard
        state={state}
        selected={selected}
        assigned={state.assigned?.length || 0}
        poolDisplay={poolDisplay}
        highlightEnergyKey={energyKey}
        tickFloat={tickFloats}
        upgradeOwned={selected?.kind === "upgrade" ? (owned[selected.spark.collectible_id] || 0) : 0}
        onBuyNext={buyNextSpark}
        onUpgrade={upgradeSpark}
        onDeselect={() => setSelected(null)}
      />

      {/* LEFT crackers (3 steps) */}
      {["heavyweight", "middleweight", "cruiserweight"].map((cls) => (
        <Cracker
          key={cls}
          cls={cls}
          data={state.crackers[cls]}
          slot={CRACKER_SLOTS[cls]}
          onClickCracker={handleClickCracker}
          pulseIdx={pulse.cls === cls ? pulse.idx : null}
          pulse={pulse.cls === cls ? pulse : null}
          selected={selected}
        />
      ))}
      {/* RIGHT crackers (2 steps + 1 reserved) */}
      {["tag_team", "wildcard"].map((cls) => (
        <Cracker
          key={cls}
          cls={cls}
          data={state.crackers[cls]}
          slot={CRACKER_SLOTS[cls]}
          onClickCracker={handleClickCracker}
          pulseIdx={pulse.cls === cls ? pulse.idx : null}
          pulse={pulse.cls === cls ? pulse : null}
          selected={selected}
        />
      ))}
      <ReservedSlot slot={CRACKER_SLOTS.reserved} />

      {tut && (
        <PyroTutorial
          stage={tut}
          sparkSelector={(() => {
            const cls = Object.keys(state.crackers).find((c) => state.crackers[c].unlocked_count > 0);
            return cls ? `[data-testid="pyro-spark-${cls}-0"]` : null;
          })()}
          onReward={async () => { try { await api.post("/pyro/tutorial/reward"); await load(); } catch {} }}
          onDone={() => setTut(null)}
        />
      )}

      {/* Wrestlers at the entrance gate */}
      <div className="absolute left-1/2 -translate-x-1/2 flex items-end justify-center gap-2 sm:gap-4 flex-wrap z-10"
           style={{ bottom: "calc(var(--vh, 1vh) * 5)", maxWidth: "min(calc(var(--vw, 1vw) * 90), 760px)" }}>
        {state.assigned?.length ? (() => {
          // Active wrestler always stands in the middle; the others flank him.
          const others = state.assigned.filter(x => !x.is_active);
          const act = state.assigned.find(x => x.is_active);
          const half = Math.ceil(others.length / 2);
          return [...others.slice(0, half), ...(act ? [act] : []), ...others.slice(half)];
        })().map((w) => (
          <StageWrestler
            key={w.wrestler_id}
            w={w}
            promo={promo}
            active={w.is_active}
            tickFloat={tickFloats ? { id: tickFloats.id, value: w.is_active ? tickFloats.value : (w.passive_rate_per_min || 0) / 60 } : null}
          />
        )) : (
          <div className="text-center px-6 py-4 rounded-lg border border-dashed"
               style={{ borderColor: "#FF950066", background: "rgba(10,5,26,0.75)" }}>
            <div className="font-heading text-sm text-[#FF9500] mb-1">Empty stage</div>
            <div className="text-[11px] font-mono text-[#A89BCC]">Send wrestlers from the Roster to earn Energy.</div>
          </div>
        )}
      </div>

      {/* Hint */}
      <div className="hidden xl:block [@media(max-height:799px)]:hidden absolute left-1/2 -translate-x-1/2 z-10 text-center" style={{ bottom: "calc(var(--vh, 1vh) * 1)" }}>
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#FFD70055] bg-[#0A051ACC] text-[10px] font-mono text-[#A89BCC]">
          <Sparkles size={12} className="text-[#FFD700]" />
          Click cracker base to unlock · click spark to upgrade · send wrestlers from their Wrestler page
        </div>
      </div>
    </div>
  );
}
